import React, { useCallback, useState, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Button,
  BlockStack,
  InlineStack,
  TextField,
  Spinner,
} from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import '../modal-overrides.css';
import '../dashboard.css';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';

const SERVER_URL = getServerURL();

const SettingsPage = ({ setToastMessage, setToastActive }) => {
  const navigate = useNavigate();
  const [previewerSettings, setPreviewerSettings] = useState(null);
  const [generationSettings, setGenerationSettings] = useState(null);
  const [designSettings, setDesignSettings] = useState(null);
  const [backupSettings, setBackupSettings] = useState(null);
  const [isLoadingPreviewer, setIsLoadingPreviewer] = useState(false);
  const [isLoadingGeneration, setIsLoadingGeneration] = useState(false);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  const [isLoadingBackup, setIsLoadingBackup] = useState(false);
  const [isSavingPreviewer, setIsSavingPreviewer] = useState(false);
  const [isSavingGeneration, setIsSavingGeneration] = useState(false);
  const [isSavingDesign, setIsSavingDesign] = useState(false);
  const [isSavingBackup, setIsSavingBackup] = useState(false);
  const [previewerRunpodId, setPreviewerRunpodId] = useState('');
  const [generationRunpodId, setGenerationRunpodId] = useState('');
  const [designRunpodId, setDesignRunpodId] = useState('');
  const [backupRunpodId, setBackupRunpodId] = useState('');

  // Fetch Previewer settings
  const fetchPreviewerSettings = useCallback(async () => {
    setIsLoadingPreviewer(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/previewer`, {
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setPreviewerSettings(result.data[0]);
        setPreviewerRunpodId(result.data[0].RUNPOD_ID || '');
      }
    } catch (error) {
      console.error('Failed to fetch previewer settings:', error);
      setToastMessage('Failed to fetch previewer settings');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsLoadingPreviewer(false);
    }
  }, []);

  // Fetch Generation settings
  const fetchGenerationSettings = useCallback(async () => {
    setIsLoadingGeneration(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/generation`, {
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setGenerationSettings(result.data[0]);
        setGenerationRunpodId(result.data[0].RUNPOD_ID || '');
      }
    } catch (error) {
      console.error('Failed to fetch generation settings:', error);
      setToastMessage('Failed to fetch generation settings');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsLoadingGeneration(false);
    }
  }, []);

  // Fetch Design settings
  const fetchDesignSettings = useCallback(async () => {
    setIsLoadingDesign(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/design`, {
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setDesignSettings(result.data[0]);
        setDesignRunpodId(result.data[0].RUNPOD_ID || '');
      }
    } catch (error) {
      console.error('Failed to fetch design settings:', error);
      setToastMessage('Failed to fetch design settings');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsLoadingDesign(false);
    }
  }, []);

  // Fetch Backup settings
  const fetchBackupSettings = useCallback(async () => {
    setIsLoadingBackup(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/backup`, {
        headers: authService.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data && result.data.length > 0) {
        setBackupSettings(result.data[0]);
        setBackupRunpodId(result.data[0].RUNPOD_ID || '');
      }
    } catch (error) {
      console.error('Failed to fetch backup settings:', error);
      setToastMessage('Failed to fetch backup settings');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsLoadingBackup(false);
    }
  }, []);

  // Save Previewer settings
  const savePreviewerSettings = useCallback(async () => {
    if (!previewerSettings || !previewerRunpodId.trim()) {
      setToastMessage('Please enter a valid RUNPOD ID');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsSavingPreviewer(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/previewer/${previewerSettings._id}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          RUNPOD_ID: previewerRunpodId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setToastMessage('Previewer settings saved successfully');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 3000);
        // Refresh the settings
        fetchPreviewerSettings();
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save previewer settings:', error);
      setToastMessage(`Failed to save previewer settings: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsSavingPreviewer(false);
    }
  }, [previewerSettings, previewerRunpodId, fetchPreviewerSettings]);

  // Save Generation settings
  const saveGenerationSettings = useCallback(async () => {
    if (!generationSettings || !generationRunpodId.trim()) {
      setToastMessage('Please enter a valid RUNPOD ID');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsSavingGeneration(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/generation/${generationSettings._id}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          RUNPOD_ID: generationRunpodId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setToastMessage('Generation settings saved successfully');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 3000);
        // Refresh the settings
        fetchGenerationSettings();
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save generation settings:', error);
      setToastMessage(`Failed to save generation settings: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsSavingGeneration(false);
    }
  }, [
    generationSettings,
    generationRunpodId,
    fetchGenerationSettings
  ]);

  // Save Design settings
  const saveDesignSettings = useCallback(async () => {
    if (!designSettings || !designRunpodId.trim()) {
      setToastMessage('Please enter a valid RUNPOD ID');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsSavingDesign(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/design/${designSettings._id}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          RUNPOD_ID: designRunpodId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setToastMessage('Design settings saved successfully');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 3000);
        fetchDesignSettings();
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save design settings:', error);
      setToastMessage(`Failed to save design settings: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsSavingDesign(false);
    }
  }, [designSettings, designRunpodId, fetchDesignSettings]);

  // Save Backup settings
  const saveBackupSettings = useCallback(async () => {
    if (!backupSettings || !backupRunpodId.trim()) {
      setToastMessage('Please enter a valid RUNPOD ID');
      setToastActive(true);
      setTimeout(() => setToastActive(false), 3000);
      return;
    }

    setIsSavingBackup(true);
    try {
      const response = await fetch(`${SERVER_URL}/settings/backup/${backupSettings._id}`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({
          RUNPOD_ID: backupRunpodId.trim()
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setToastMessage('Backup settings saved successfully');
        setToastActive(true);
        setTimeout(() => setToastActive(false), 3000);
        fetchBackupSettings();
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save backup settings:', error);
      setToastMessage(`Failed to save backup settings: ${error.message}`);
      setToastActive(true);
      setTimeout(() => setToastActive(false), 5000);
    } finally {
      setIsSavingBackup(false);
    }
  }, [backupSettings, backupRunpodId, fetchBackupSettings]);

  // Load settings on component mount
  useEffect(() => {
    fetchPreviewerSettings();
    fetchGenerationSettings();
    fetchDesignSettings();
    fetchBackupSettings();
  }, [fetchPreviewerSettings, fetchGenerationSettings, fetchDesignSettings, fetchBackupSettings]);

  const saveSettings = useCallback(async () => {
    const settings = {
      theme: 'light'
    };

    try {
      // Call the API to update .env file
      const response = await fetch('/api/update-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setToastMessage('Settings Saved');
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 3000);
      } else {
        console.error('Failed to save settings - server responded with:', response.status);
        // Show error toast or fallback to localStorage
        setToastMessage('Settings Saved');
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to save settings - network error:', error);
      // Fallback to localStorage if server is not available
      try {
        localStorage.setItem('storiesBySnapsSettings', JSON.stringify(settings));
        setToastMessage('Settings Saved');
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 3000);
      } catch (localError) {
        console.error('Failed to save to localStorage:', localError);
      }
    }
  }, []);

  return (
    <Layout>
      <Layout.Section>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32 }}>
            {/* Previewer Settings Card */}
            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff' }}>
              <div style={{ padding: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <Text variant="headingMd" as="h2" fontWeight="bold">Previewer Settings</Text>
                </div>

                {isLoadingPreviewer ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">Loading previewer settings...</Text>
                  </div>
                ) : previewerSettings ? (
                  <BlockStack gap="400">
                    <div>
                      <Text variant="headingMd" as="h3">Runpod Server Configuration</Text>
                      <Text variant="bodyMd" tone="subdued">
                        Configure the Runpod server ID for the previewer service
                      </Text>
                    </div>

                    <div>
                      <TextField
                        label="RUNPOD ID"
                        value={previewerRunpodId}
                        onChange={setPreviewerRunpodId}
                        placeholder="Enter Runpod Server ID"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <Button
                        variant="primary"
                        onClick={savePreviewerSettings}
                        loading={isSavingPreviewer}
                      >
                        Save Previewer Settings
                      </Button>
                    </div>
                  </BlockStack>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" tone="subdued">No previewer settings found</Text>
                    <Button onClick={fetchPreviewerSettings} loading={isLoadingPreviewer}>
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Generation Settings Card */}
            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff' }}>
              <div style={{ padding: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <Text variant="headingMd" as="h2" fontWeight="bold">Generation Settings</Text>
                </div>

                {isLoadingGeneration ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">Loading generation settings...</Text>
                  </div>
                ) : generationSettings ? (
                  <BlockStack gap="400">
                    <div>
                      <Text variant="headingMd" as="h3">Runpod Server Configuration</Text>
                      <Text variant="bodyMd" tone="subdued">
                        Configure the Runpod server ID for the generation service
                      </Text>
                    </div>

                    <div>
                      <TextField
                        label="RUNPOD ID"
                        value={generationRunpodId}
                        onChange={setGenerationRunpodId}
                        placeholder="Enter Runpod Server ID"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <InlineStack align="space-between" blockAlign="center">
                        <Text variant="bodyMd" tone="subdued">
                          Manage regeneration providers and model catalog from a dedicated page.
                        </Text>
                        <Button onClick={() => navigate('/settings/ai-models')}>
                          Open AI Models
                        </Button>
                      </InlineStack>
                    </div>

                    <div>
                      <Button
                        variant="primary"
                        onClick={saveGenerationSettings}
                        loading={isSavingGeneration}
                      >
                        Save Generation Settings
                      </Button>
                    </div>
                  </BlockStack>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" tone="subdued">No generation settings found</Text>
                    <Button onClick={fetchGenerationSettings} loading={isLoadingGeneration}>
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Design Settings Card */}
            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff' }}>
              <div style={{ padding: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <Text variant="headingMd" as="h2" fontWeight="bold">Design Settings</Text>
                </div>

                {isLoadingDesign ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">Loading design settings...</Text>
                  </div>
                ) : designSettings ? (
                  <BlockStack gap="400">
                    <div>
                      <Text variant="headingMd" as="h3">Runpod Server Configuration</Text>
                      <Text variant="bodyMd" tone="subdued">
                        Configure the Runpod server ID for the design service
                      </Text>
                    </div>

                    <div>
                      <TextField
                        label="RUNPOD ID"
                        value={designRunpodId}
                        onChange={setDesignRunpodId}
                        placeholder="Enter Runpod Server ID"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <Button
                        variant="primary"
                        onClick={saveDesignSettings}
                        loading={isSavingDesign}
                      >
                        Save Design Settings
                      </Button>
                    </div>
                  </BlockStack>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" tone="subdued">No design settings found</Text>
                    <Button onClick={fetchDesignSettings} loading={isLoadingDesign}>
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Backup Settings Card */}
            <Card style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderRadius: 16, background: '#fff' }}>
              <div style={{ padding: 32 }}>
                <div style={{ marginBottom: 24 }}>
                  <Text variant="headingMd" as="h2" fontWeight="bold">Backup Settings</Text>
                </div>

                {isLoadingBackup ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">Loading backup settings...</Text>
                  </div>
                ) : backupSettings ? (
                  <BlockStack gap="400">
                    <div>
                      <Text variant="headingMd" as="h3">Runpod Server Configuration</Text>
                      <Text variant="bodyMd" tone="subdued">
                        Configure the Runpod server ID for the backup service
                      </Text>
                    </div>

                    <div>
                      <TextField
                        label="RUNPOD ID"
                        value={backupRunpodId}
                        onChange={setBackupRunpodId}
                        placeholder="Enter Runpod Server ID"
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <Button
                        variant="primary"
                        onClick={saveBackupSettings}
                        loading={isSavingBackup}
                      >
                        Save Backup Settings
                      </Button>
                    </div>
                  </BlockStack>
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" tone="subdued">No backup settings found</Text>
                    <Button onClick={fetchBackupSettings} loading={isLoadingBackup}>
                      Retry
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </Layout.Section>
    </Layout>
  );
};

export default SettingsPage;
